<template>
  <template v-if="props.datePickerVersion === '1'">
    <div class="spa-datepicker" :class="{ 'is-invalid': error.hasErr }">
      <MDBDatepicker
        v-model="input"
        autocomplete="off"
        format="MM/DD/YYYY"
        placeholder="MM/DD/YYYY"
        inline
        required
        v-bind="$attrs"
        :filter="(date) => filterDate(date)"
      />
      <span v-if="error.hasErr">{{ error.errMsg }}</span>
    </div>
  </template>
  <template v-if="props.datePickerVersion === '2'">
    <SPADatepicker2 v-bind="$attrs">
      <template #total-selected>
        <slot name="total-selected" />
      </template>
    </SPADatepicker2>
  </template>
</template>

<script setup>
import { MDBDatepicker } from 'mdb-vue-ui-kit';
import { ref, computed, watch } from 'vue';
import SPADatepicker2 from './SPADatepicker2.vue';
import {
  TODAY,
  isEqualOrGreaterThanDateFrom,
  isEqualOrLessThanDateTo,
  isWithin90Day,
  isDateFutureDated,
  isDateInvalid,
  isFormatStandard,
  isWithin31Day,
} from '../helpers/dateParser';
const props = defineProps({
  endDate: {
    type: String,
  },
  startDate: {
    type: String,
  },
  checkIfIsWithin90Day: {
    type: Boolean,
    default: false,
  },
  standardFormat: {
    type: Boolean,
    default: false,
  },
  checkIfIsWithin31Day: {
    type: Boolean,
    default: false,
  },
  enableFutureDate: {
    type: Boolean,
    default: false,
  },
  disableDatesOnTimeSheetLock: {
    type: Object,
  },
  datePickerVersion: {
    type: String,
    default: '1',
    validator: (val) => ['1', '2'].includes(val),
  },
});
const input = ref('');
const error = computed(() => {
  if (!input.value) {
    return { hasErr: false, errMsg: '' };
  }
  if (checkIfDateNotValid(input.value)) {
    return { hasErr: true, errMsg: 'Invalid date or date format' };
  }
  if (!props.enableFutureDate && isDateFutureDated(input.value)) {
    return { hasErr: true, errMsg: 'Must be equal or less than Date Today' };
  }
  if (checkStartDateValidity(input.value)) {
    return { hasErr: true, errMsg: 'Must be equal or greater than Date From' };
  }
  if (checkEndDateValidity(input.value)) {
    return { hasErr: true, errMsg: 'Must be equal or less than Date To' };
  }
  if (checkWithin90Day(input.value)) {
    return { hasErr: true, errMsg: 'Must be within 90-day period' };
  }
  if (checkWithin31Day(input.value)) {
    return { hasErr: true, errMsg: 'Must be within 31-day period' };
  }
  return { hasErr: false, errMsg: '' };
});
const checkIfDateNotValid = (date) => {
  return isDateInvalid(date) || checkIfNotStandard(date);
};
const checkIfNotStandard = (date) => {
  return props.standardFormat && !isFormatStandard(date);
};
const checkStartDateValidity = (date) => {
  return props.startDate && isEqualOrGreaterThanDateFrom(props.startDate, date);
};
const checkEndDateValidity = (date) => {
  return props.endDate && isEqualOrLessThanDateTo(date, props.endDate);
};
const checkWithin90Day = (date) => {
  if (props.checkIfIsWithin90Day) {
    return (
      (props.endDate && isWithin90Day(date, props.endDate)) || (props.startDate && isWithin90Day(props.startDate, date))
    );
  }
  return false;
};
const checkWithin31Day = (date) => {
  if (props.checkIfIsWithin31Day) {
    return (
      (props.endDate && isWithin31Day(date, props.endDate)) || (props.startDate && isWithin31Day(props.startDate, date))
    );
  }
  return false;
};

watch(error, (newError) => {
  if (input.value) {
    if (newError.hasErr) {
      emit('hasError', true);
      return;
    }
    emit('hasError', false);
  }
});

const filterDate = (date) => {
  if (!props.enableFutureDate) return date > TODAY;
};
const emit = defineEmits(['hasError']);
</script>
<style lang="scss" scoped>
.spa-datepicker {
  font-weight: 400;
  margin-bottom: 8px;
  :deep(input.form-control) {
    color: $neutral-800;
    padding: 12px;
    line-height: 24px;
    background-color: $neutral-0;
    &:disabled {
      background-color: $neutral-10;
    }
    ~ button {
      right: 6px;
    }
    ~ button .far {
      color: $neutral-50;
    }

    & ~ .form-notch .form-notch-leading,
    & ~ .form-notch .form-notch-middle,
    & ~ .form-notch .form-notch-trailing {
      border-color: $neutral-50;
    }

    & ~ .form-label {
      color: $neutral-80;
      padding-top: 12px;
    }
    &.active ~ .form-label,
    &:focus ~ .form-label {
      padding-top: 6px;
    }
    &:focus {
      & ~ .form-notch .form-notch-leading,
      & ~ .form-notch .form-notch-middle,
      & ~ .form-notch .form-notch-trailing {
        border-color: $green-300;
      }
      ~ .form-label,
      ~ button .far {
        color: $green-300;
      }

      &::placeholder {
        color: $neutral-80;
      }
    }
  }
  &.is-invalid {
    :deep(input.form-control) {
      margin-bottom: 0;
      & ~ .form-label {
        color: $red-300;
      }

      & ~ .form-notch .form-notch-leading,
      & ~ .form-notch .form-notch-middle,
      & ~ .form-notch .form-notch-trailing {
        border-color: $red-300;
      }

      ~ button .far {
        color: $red-300;
      }
    }

    ul {
      padding-left: 20px;
      margin-bottom: 0;
    }

    ul li,
    span {
      color: $red-300;
      font-size: 12px;
      line-height: 16px;
    }
  }
  & .datepicker-toggle-button {
    right: -12px;
  }
}
</style>

<style lang="scss">
// Because of its appendToBody nature, it cannot be scoped.
.datepicker .datepicker-dropdown-container > * {
  font-weight: 400;
}
.datepicker-previous-button,
.datepicker-next-button {
  color: $green-300;
}
.datepicker-cell.selected .datepicker-cell-content {
  color: $neutral-0;
  background-color: $green-300;
}
.datepicker-cell.current .datepicker-cell-content {
  position: relative;
  border-color: transparent;

  &:before {
    content: '•';
    position: absolute;
    bottom: -12px;
    left: 0;
    right: 0;
    color: $green-300;
  }
}
.datepicker-small-cell-content {
  border-radius: 0;
}
.datepicker-cell:not(.selected).focused .datepicker-cell-content {
  background-color: unset;
}
.datepicker-cell:not(.disabled):not(.selected):hover .datepicker-cell-content {
  color: $green-300;
  background-color: unset;
}
</style>
